import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {t} from 'i18next';
import _ from 'lodash';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';
import StandardReports from '../../StandardReports';
import {
  ColumnConfig,
  Reports,
  RightPanelConfig,
} from '../../config/reportTypes';
import {
  useGetLeftPanelData,
  useLeftPanelReport,
  useSaveAssignGroupGridReportData,
  useGetReviewReport,
  useExportReviewProgressAssignment,
  useGetReportState,
} from '../../../../../hooks/reporting/useReporting';
import {
  getSaveAssignGroupGridReportDataPayload,
  mergeApiDataIntoConfig,
  transformLeftPanelDataToReviewResultsSources,
  updateApplyButtonState,
  getDefaultSorting,
  getAppliedFilterValueObject,
  getReviewProgressAssignmentExportPayload,
  getReviewProgressAssignmentReportXmlPayload,
} from './Helper';
import {
  STRING_PATH,
  INITIAL_PAGE_SIZE,
  PAGE_SIZE_INCREMENT,
  DEFAULT_HIDDEN_COLUMNS,
  PageSortConfigType,
  SavedReportData,
  SaveReportPayload,
  getBaseColumns,
  getSummaryColumns,
  transformReviewReportData,
  getReportIdFromUrl,
  hasReportLabelInUrl,
  getReportKey,
  applyColumnVisibility,
  calculateEffectiveSelectedCustomColumns,
  createInitialPaginationSortConfig,
  createInitialSavedReportData,
  createInitialSaveReportPayload,
} from './utils';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import './ReviewProgressAssignmentContainer.scss';
import {
  REPORT_PAGE_SIZE_OPTIONS,
  ToastType,
} from '../../../../../shared/constants';
import {ToastMessageConfigType} from '../../../../../api/types/ingestionApi.type';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import ArrWaitToCompleteModalWrapper from '../../component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import GroupedAssignmentTables from './component/GroupedAssignmentTables';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {Box} from '@mui/material';

dayjs.extend(utc);

const ReviewProgressAssignmentContainer: React.FC = () => {
  const location = useLocation();
  const isSavedReportOpen = useRef<boolean>(false);
  const isResetExecuted = useRef<boolean>(false);
  const initialJsonConfig = useRef<any | null>(null);
  const loadedReportIdFromUrl = useRef<number | null>(null);

  const [savedReportData, setSavedReportData] = useState<SavedReportData>(
    createInitialSavedReportData()
  );

  const [savedFilterValues, setSavedFilterValues] = useState<
    Record<string, any>
  >({});

  // Track current filter values including toggle state
  const [currentFilterValues, setCurrentFilterValues] = useState<
    Record<string, any>
  >({});

  const [reportConfig, setReportConfig] = useState<any | null>(null);

  const [saveReportPayload, setSaveReportPayload] = useState<SaveReportPayload>(
    createInitialSaveReportPayload()
  );

  const [paginationSortConfig, setPaginationConfig] =
    useState<PageSortConfigType>(
      createInitialPaginationSortConfig(getDefaultSorting)
    );

  const paginationSortConfigRef =
    useRef<PageSortConfigType>(paginationSortConfig);

  const [isQueryRun, setIsQueryRun] = useState(false);

  const [reportGridData, setReportGridData] = useState<any[]>([]);
  const [rawApiResponse, setRawApiResponse] = useState<any>(null);
  const [hasMoreData, setHasMoreData] = useState<boolean>(false);
  const [currentPageSize, setCurrentPageSize] =
    useState<number>(INITIAL_PAGE_SIZE);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [
    accumulatedAssignmentProgressList,
    setAccumulatedAssignmentProgressList,
  ] = useState<any[]>([]);

  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });

  // Track savedData for RightPanel to restore column visibility after export
  const [savedDataForRightPanel, setSavedDataForRightPanel] = useState<{
    selectedCustomColumns: number[];
  } | null>(null);

  const {
    data: leftPanelData,
    isLoading: isLoadingLeftPanelData,
    isError: isErrorLeftPanelData,
    error: leftPanelDataError,
    refetch: fetchLeftPanelData,
  } = useGetLeftPanelData(0);

  const {
    mutateAsync: saveAssignGroupGridReportDataRequest,
    isPending: isSavingReportData,
  } = useSaveAssignGroupGridReportData();

  /** Get Review Report Data Request */
  const {mutateAsync: getReviewReportRequest} = useGetReviewReport();

  /** Export Review Progress Assignment Report */
  const {
    mutateAsync: exportReviewProgressAssignmentRequest,
    isPending: isExportingReport,
  } = useExportReviewProgressAssignment();

  /** Get Left Panel Report Data Request when saved report is opened */
  const reportIdFromUrl = useMemo(() => {
    const reportId = getReportIdFromUrl(location.pathname, location.search);
    if (reportId > 0) {
      isSavedReportOpen.current = true;
    }
    return reportId;
  }, [location.pathname, location.search]);

  // Check if ReportLabel is present in URL (notification case)
  const hasReportLabel = useMemo(() => {
    return hasReportLabelInUrl(location.search);
  }, [location.search]);

  /** Get Report State when coming from notification URL (without ReportLabel) */
  const {
    refetch: fetchReportState,
    isLoading: isReportStateLoading,
    isError: isReportStateError,
    data: savedReportStateData,
    error: reportStateError,
  } = useGetReportState(
    reportIdFromUrl > 0 && !hasReportLabel ? reportIdFromUrl.toString() : '0',
    reportIdFromUrl > 0 && !hasReportLabel
  );

  const {
    data: leftPanelSavedReportData,
    isLoading: isLoadingSavedLeftPanelReport,
    isError: isErrorSavedLeftPanelReport,
    error: leftPanelSavedReportError,
    refetch: _fetchSavedLeftPanelReportData, // Auto-fetches when enabled, kept for potential manual refetch
  } = useLeftPanelReport(
    reportIdFromUrl,
    reportIdFromUrl > 0 && hasReportLabel // Only enable for saved report cases (with ReportLabel), not notification cases
  );

  const reportKey = useMemo(() => {
    return getReportKey(location.pathname);
  }, [location.pathname]);

  // Use progressReportFilteration from state-report when coming from notification (without ReportLabel)
  // Otherwise use leftPanelSavedReportData from leftpanel-report
  const effectiveSavedReportData = useMemo(() => {
    // When coming from notification (without ReportLabel), use progressReportFilteration from state-report
    if (!hasReportLabel && savedReportStateData?.progressReportFilteration) {
      const filteration = savedReportStateData.progressReportFilteration;
      // Transform progressReportFilteration to match LeftPanelReportResponse structure
      return {
        selectedAssignments: filteration.selectedAssignments ?? [],
        selectedAssignmentClassification:
          filteration.selectedAssignmentClassification ?? [],
        selectedCustomColumns: filteration.selectedCustomColumns ?? [],
        include_Assignment_Detail: 1, // Default value
      } as any;
    }
    // Otherwise use leftPanelSavedReportData from leftpanel-report
    return leftPanelSavedReportData;
  }, [
    hasReportLabel,
    savedReportStateData?.progressReportFilteration,
    leftPanelSavedReportData,
  ]);

  // Transform review report response to grid data format
  const transformReviewReportDataWrapper = useCallback(
    (response: any, isLoadMore: boolean = false) => {
      return transformReviewReportData({
        response,
        isLoadMore,
        accumulatedAssignmentProgressList,
        setAccumulatedAssignmentProgressList,
        setRawApiResponse,
        setCurrentPageSize,
      });
    },
    [
      accumulatedAssignmentProgressList,
      setAccumulatedAssignmentProgressList,
      setRawApiResponse,
      setCurrentPageSize,
    ]
  );

  useEffect(() => {
    setHasMoreData(
      rawApiResponse?.assignmentProgressList.length <
        savedFilterValues?.selectedAssignments?.length
    );
  }, [
    rawApiResponse?.assignmentProgressList,
    savedFilterValues?.selectedAssignments,
  ]);

  useEffect(() => {
    if (loadedReportIdFromUrl.current === null) {
      loadedReportIdFromUrl.current = reportIdFromUrl;
      loadReportConfig().then(() => {
        if (
          (location.state?.fromReportLanding ||
            location.state?.isBackgroundReport ||
            reportIdFromUrl !== 0) &&
          reportIdFromUrl !== 0
        ) {
          // If ReportLabel is not present, call useGetReportState (notification case)
          // Note: leftpanel-report API is automatically called by useLeftPanelReport hook
          // when reportIdFromUrl > 0, so no need to call it manually
          if (!hasReportLabel) {
            fetchReportState();
          }
          // Always fetch base left panel data to populate Assignment Group tree
          // This is needed even when coming from saved report or notification
          if (!leftPanelData && !isLoadingLeftPanelData) {
            fetchLeftPanelData();
          }
        } else {
          fetchLeftPanelData();
        }
      });
    } else if (
      reportIdFromUrl !== 0 &&
      loadedReportIdFromUrl.current !== null
    ) {
      // id updated in url so reset all state and fetch report data again
      isResetExecuted.current = false;
      setIsQueryRun(false);
      resetStateToInitial();
      setSavedFilterValues({});
      setCurrentFilterValues({});
      setReportConfig(null);
      setSavedReportData(createInitialSavedReportData());
      paginationSortConfigRef.current =
        createInitialPaginationSortConfig(getDefaultSorting);
      setPaginationConfig(paginationSortConfigRef.current);
      loadedReportIdFromUrl.current = reportIdFromUrl;

      // If ReportLabel is not present, call useGetReportState (notification case)
      // For notification cases, we don't call leftpanel-report API - we use progressReportFilteration from state-report
      // For saved report cases (with ReportLabel), leftpanel-report API is called by useLeftPanelReport hook
      if (!hasReportLabel) {
        fetchReportState();
        // For notification cases, fetch leftPanelData directly after state-report (no need to wait for leftpanel-report)
        if (!leftPanelData && !isLoadingLeftPanelData) {
          fetchLeftPanelData();
        }
      }
      // For saved report cases, leftPanelData will be fetched after leftpanel-report completes
      // See useEffect for leftPanelSavedReportData below
    }
  }, [reportIdFromUrl, hasReportLabel, fetchReportState]);

  useEffect(() => {
    if (isErrorLeftPanelData && leftPanelDataError) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          (leftPanelDataError as any)?.response?.data?.title ??
          t(STRING_PATH + 'reportConfigAPIError'),
      });
    }
  }, [isErrorLeftPanelData, leftPanelDataError]);

  // Handle useGetReportState response when coming from notification (without ReportLabel)
  useEffect(() => {
    if (!isReportStateLoading && !isReportStateError && savedReportStateData) {
      // For notification cases (without ReportLabel), we use state-report data
      // No need to call leftpanel-report API - data is already in progressReportFilteration

      // Check if assignmentProgressReportData exists in the response (notification case)
      if (savedReportStateData.assignmentProgressReportData) {
        // Use assignmentProgressReportData directly from savedReportStateData
        const response = {
          assignmentProgressList:
            savedReportStateData.assignmentProgressReportData,
        };
        const transformedData = transformReviewReportDataWrapper(
          response,
          false
        );
        setReportGridData(transformedData);
        setIsQueryRun(true);
        // Filter values will be set via getAppliedFilterValueObject when leftPanelData is available
        // This ensures proper transformation of the data
        // effectiveSavedReportData already uses progressReportFilteration from state-report

        // If leftPanelData is already available, trigger config re-merge with saved data
        // This is important when navigating from the same page where leftPanelData is cached
        if (leftPanelData && initialJsonConfig.current) {
          const transformedLeftPanelData =
            transformLeftPanelDataToReviewResultsSources(leftPanelData);
          setReportConfig(
            mergeApiDataIntoConfig(
              initialJsonConfig.current,
              transformedLeftPanelData,
              effectiveSavedReportData
            )
          );
          setSavedFilterValues(
            getAppliedFilterValueObject(
              transformedLeftPanelData,
              effectiveSavedReportData
            )
          );
        }
      }
    } else if (!isReportStateLoading && isReportStateError) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          (reportStateError as any)?.response?.data?.title ??
          t(STRING_PATH + 'reportStateAPIError'),
      });
    }
  }, [
    isReportStateError,
    isReportStateLoading,
    savedReportStateData,
    reportIdFromUrl,
    leftPanelData,
    fetchLeftPanelData,
    effectiveSavedReportData,
  ]);

  useEffect(() => {
    if (
      !isLoadingLeftPanelData &&
      !isErrorLeftPanelData &&
      leftPanelData &&
      initialJsonConfig.current
    ) {
      const transformedData =
        transformLeftPanelDataToReviewResultsSources(leftPanelData);

      // Only wait for saved report data if we're still loading it AND we have a reportId
      // If it's null after loading completes, proceed anyway (notification link without ReportLabel)
      // For notification case, we don't wait for leftpanel-report since we use state-report data
      if (
        isLoadingSavedLeftPanelReport &&
        effectiveSavedReportData == null &&
        reportIdFromUrl !== 0 &&
        hasReportLabel // Only wait if coming from saved report (with ReportLabel)
      ) {
        // Wait for saved report data to finish loading before merging config
        // But still merge config with tree data to populate the tree structure
        setReportConfig(
          mergeApiDataIntoConfig(
            initialJsonConfig.current,
            transformedData,
            undefined
          )
        );
        return;
      }

      // Always merge config with leftPanelData and effectiveSavedReportData
      // This ensures the tree is populated with both structure and selected values
      // Re-merge when effectiveSavedReportData becomes available (important when navigating from same page)
      setReportConfig(
        mergeApiDataIntoConfig(
          initialJsonConfig.current,
          transformedData,
          effectiveSavedReportData
        )
      );

      // When coming from a link, set the saved report data and filter values
      // Don't call assign-group-grid-result API - data will come from review-report API after notification
      if (location.state?.isBackgroundReport || reportIdFromUrl !== 0) {
        setSavedReportData({
          reportId: reportIdFromUrl,
        });
        setSavedFilterValues(
          getAppliedFilterValueObject(transformedData, effectiveSavedReportData)
        );
        // Initialize saveReportPayload with saved custom columns
        const savedCustomColumns =
          effectiveSavedReportData?.selectedCustomColumns;
        setSaveReportPayload((prev) => ({
          ...prev,
          reportId: reportIdFromUrl,
          selectedCustomColumns:
            savedCustomColumns && savedCustomColumns.length > 0
              ? savedCustomColumns
              : null, // null means no saved selection, will use defaults
        }));
        // Don't call fetchReportData here - data will be loaded via review-report API
      }
    }
  }, [
    leftPanelData,
    isLoadingLeftPanelData,
    isErrorLeftPanelData,
    reportKey,
    effectiveSavedReportData,
    isLoadingSavedLeftPanelReport,
    reportIdFromUrl,
    location.state,
    hasReportLabel,
  ]);

  useEffect(() => {
    // This useEffect only runs for saved report cases (with ReportLabel)
    // For notification cases (without ReportLabel), we use state-report data directly
    if (!hasReportLabel) {
      return;
    }

    // Check if this is a new navigation by comparing current reportId with loaded one
    const isNewNavigation = loadedReportIdFromUrl.current !== reportIdFromUrl;

    if (
      !isLoadingSavedLeftPanelReport &&
      leftPanelSavedReportData &&
      !isErrorSavedLeftPanelReport
    ) {
      // Skip if this is stale data from previous navigation
      if (isNewNavigation && loadedReportIdFromUrl.current !== null) {
        return;
      }

      // Now that leftpanel-report has completed, fetch left-panel data if not available
      // This ensures proper sequencing: leftpanel-report first, then left-panel
      if (leftPanelData && initialJsonConfig.current) {
        // If leftPanelData is already available, re-merge config with saved report data
        const transformedData =
          transformLeftPanelDataToReviewResultsSources(leftPanelData);
        setReportConfig(
          mergeApiDataIntoConfig(
            initialJsonConfig.current,
            transformedData,
            effectiveSavedReportData
          )
        );
        setSavedFilterValues(
          getAppliedFilterValueObject(transformedData, effectiveSavedReportData)
        );
        // Initialize saveReportPayload with saved custom columns
        const savedCustomColumns =
          effectiveSavedReportData?.selectedCustomColumns;
        setSaveReportPayload((prev) => ({
          ...prev,
          reportId: reportIdFromUrl,
          selectedCustomColumns:
            savedCustomColumns && savedCustomColumns.length > 0
              ? savedCustomColumns
              : null, // null means no saved selection, will use defaults
        }));
      } else {
        // Fetch left-panel data after leftpanel-report has completed
        if (!leftPanelData && !isLoadingLeftPanelData) {
          fetchLeftPanelData();
        }
      }
    } else if (
      !isLoadingSavedLeftPanelReport &&
      !leftPanelSavedReportData &&
      reportIdFromUrl !== 0
    ) {
      // Skip if this is stale data from previous navigation
      if (isNewNavigation && loadedReportIdFromUrl.current !== null) {
        return;
      }
      // When leftpanel-report API returns null/empty, fetch left panel data
      // This ensures proper sequencing: leftpanel-report first, then left-panel
      if (!leftPanelData && !isLoadingLeftPanelData) {
        fetchLeftPanelData();
      }
    } else if (
      leftPanelSavedReportError &&
      isErrorSavedLeftPanelReport &&
      !isLoadingSavedLeftPanelReport
    ) {
      // Skip if this is stale data from previous navigation
      if (isNewNavigation && loadedReportIdFromUrl.current !== null) {
        return;
      }
      // If API errors, still try to fetch left panel data to proceed
      // But only after leftpanel-report has completed (even with error)
      if (!leftPanelData && !isLoadingLeftPanelData) {
        fetchLeftPanelData();
      }
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          (leftPanelSavedReportError as any)?.response?.data?.title ??
          t(STRING_PATH + 'reportConfigAPIError'),
      });
    }
  }, [
    hasReportLabel,
    leftPanelSavedReportData,
    isLoadingSavedLeftPanelReport,
    isErrorSavedLeftPanelReport,
    leftPanelSavedReportError,
    reportIdFromUrl,
    leftPanelData,
    fetchLeftPanelData,
    effectiveSavedReportData,
  ]);

  const baseColumns = useMemo<ColumnConfig[]>(() => {
    return getBaseColumns(t);
  }, [t]);

  const effectiveSelectedCustomColumns = useMemo(() => {
    return calculateEffectiveSelectedCustomColumns(
      saveReportPayload.selectedCustomColumns,
      effectiveSavedReportData?.selectedCustomColumns,
      rawApiResponse?.sourceColumnList
    );
  }, [
    effectiveSavedReportData?.selectedCustomColumns,
    saveReportPayload.selectedCustomColumns,
    rawApiResponse?.sourceColumnList,
  ]);

  const columns = useMemo<ColumnConfig[]>(() => {
    return applyColumnVisibility(baseColumns, effectiveSelectedCustomColumns);
  }, [baseColumns, effectiveSelectedCustomColumns, applyColumnVisibility]);

  // Update savedDataForRightPanel when effectiveSelectedCustomColumns changes
  useEffect(() => {
    setSavedDataForRightPanel({
      selectedCustomColumns: effectiveSelectedCustomColumns,
    });
  }, [effectiveSelectedCustomColumns]);

  // Summary columns for reviewProgress_AssignmentGroup_List (first table)
  // These columns are always visible and not affected by column selection dropdown
  const summaryColumns = useMemo<ColumnConfig[]>(() => {
    return getSummaryColumns(t);
  }, [t]);

  const handleColumnToggleAction = useCallback(
    (columnId: string) => {
      setSaveReportPayload((prev) => {
        // RightPanel passes field name, but we use index as unique key
        const columnIdStr = String(columnId).trim();

        // Find the column by field name in baseColumns
        // Use filter to ensure we get exactly one match
        const matchingColumns = baseColumns.filter(
          (col) => String(col.field).trim() === columnIdStr
        );

        // Must have exactly one match - if not, don't update
        if (matchingColumns.length !== 1) {
          return prev;
        }

        const targetCol = matchingColumns[0];

        // Verify the column has a valid index (unique key)
        if (typeof targetCol.index !== 'number') {
          return prev;
        }

        // Use index as the unique identifier for toggling
        const targetIndex = targetCol.index;
        // If null, initialize with DEFAULT_HIDDEN_COLUMNS to start from default state
        const currentHiddenColumns =
          prev.selectedCustomColumns !== null
            ? prev.selectedCustomColumns
            : DEFAULT_HIDDEN_COLUMNS;

        // Toggle: if index is in hidden list, remove it (show column); otherwise add it (hide column)
        const isCurrentlyHidden = currentHiddenColumns.includes(targetIndex);
        const nextHiddenColumns = isCurrentlyHidden
          ? currentHiddenColumns.filter((idx) => idx !== targetIndex)
          : [...currentHiddenColumns, targetIndex];

        // Ensure uniqueness and sort for consistency
        const uniqueNext = Array.from(new Set(nextHiddenColumns)).sort(
          (a, b) => a - b
        );

        return {
          ...prev,
          selectedCustomColumns: uniqueNext, // Can be [] if all columns are visible
        };
      });
    },
    [baseColumns]
  );

  const handleExportClick = useCallback(async () => {
    if (!reportConfig || !leftPanelData || !isQueryRun) return;

    const transformedData =
      transformLeftPanelDataToReviewResultsSources(leftPanelData);

    // Use selectedAssignments count as pageSize for export
    const selectedAssignments = savedFilterValues?.selectedAssignments || [];
    const exportPageSize = Array.isArray(selectedAssignments)
      ? selectedAssignments.length
      : INITIAL_PAGE_SIZE;

    const exportPayload = getReviewProgressAssignmentExportPayload(
      reportConfig,
      savedFilterValues,
      transformedData,
      paginationSortConfig.pageNumber,
      exportPageSize,
      effectiveSelectedCustomColumns
    );

    try {
      await exportReviewProgressAssignmentRequest(exportPayload);
      setToastMsgConfig({
        show: true,
        type: ToastType.Success,
        message: t('reports.common.reportExportSuccess'),
      });
      // Restore column visibility after export by updating savedDataForRightPanel
      // This triggers RightPanel's useEffect to restore the correct column visibility
      setSavedDataForRightPanel({
        selectedCustomColumns: effectiveSelectedCustomColumns,
      });
    } catch (error: any) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message:
          error?.response?.data?.title ?? t('reports.common.reportExportError'),
      });
    }
  }, [
    reportConfig,
    leftPanelData,
    isQueryRun,
    savedFilterValues,
    paginationSortConfig,
    exportReviewProgressAssignmentRequest,
    effectiveSelectedCustomColumns,
    t,
  ]);

  const payloadForSaveReport = useMemo(() => {
    if (reportConfig && savedReportData.reportId > 0 && leftPanelData) {
      const transformedData =
        transformLeftPanelDataToReviewResultsSources(leftPanelData);
      return getReviewProgressAssignmentReportXmlPayload(
        reportConfig!,
        {
          ...savedFilterValues,
          selectedCustomColumns: effectiveSelectedCustomColumns,
        },
        transformedData,
        savedReportData.reportId,
        paginationSortConfig.pageNumber,
        paginationSortConfig.pageSize
      );
    }
    return null;
  }, [
    reportConfig,
    leftPanelData,
    savedReportData,
    savedFilterValues,
    paginationSortConfig,
    effectiveSelectedCustomColumns,
  ]);

  const rightPanelHeaderConfig = useMemo<RightPanelConfig>(() => {
    return {
      subTitle: t(STRING_PATH + 'dateAsOf', {
        date: dayjs().utc().format('YYYY/MM/DD HH:mm:ss'),
      }),
      showUploadIcon: true,
      showSaveIcon: true,
      showColumnsIcon: true,
      showActionButton: false,
      onUploadClick: handleExportClick,
    };
  }, [t, payloadForSaveReport, handleExportClick]);

  const totalRecordsCount = useMemo(() => {
    return reportGridData.length;
  }, [reportGridData]);

  const loadReportConfig = useCallback(async () => {
    if (!reportKey) return;
    try {
      const data: Reports = await import(`../../config/${reportKey}.json`);
      initialJsonConfig.current = data;
      return data;
    } catch (error) {
      setToastMsgConfig({
        show: true,
        type: ToastType.Error,
        message: t(STRING_PATH + 'reportConfigAPIError'),
      });
      console.log('Error loading JSON:', error);
    }
  }, [reportKey]);

  const handleFilterChange = useCallback((values: Record<string, any>) => {
    // Store current filter values to track toggle state
    setCurrentFilterValues(values);
    setReportConfig((prevReportConfig: Reports | null) => {
      if (!prevReportConfig) return prevReportConfig;
      return updateApplyButtonState(prevReportConfig, values);
    });
  }, []);

  const resetStateToInitial = () => {
    setReportGridData([]);
    setAccumulatedAssignmentProgressList([]);
    setCurrentPageSize(INITIAL_PAGE_SIZE);
    setIsLoadingMore(false);
  };

  const executeSaveAssignGroupGridReportData = (
    values: Record<string, any>,
    isLoadMore: boolean = false,
    pageSizeOverride?: number
  ) => {
    if (!leftPanelData) return;
    const transformedData =
      transformLeftPanelDataToReviewResultsSources(leftPanelData);
    // Use effectiveSelectedCustomColumns as-is (empty array means all columns visible, no hidden columns)
    const hiddenColumns = effectiveSelectedCustomColumns;

    // For Load More, use pageSizeOverride if provided, otherwise use currentPageSize; otherwise use INITIAL_PAGE_SIZE
    const pageSizeToUse = isLoadMore
      ? (pageSizeOverride ?? currentPageSize)
      : INITIAL_PAGE_SIZE;

    const savePayload = getSaveAssignGroupGridReportDataPayload(
      reportConfig!,
      values,
      transformedData,
      paginationSortConfig.pageNumber,
      pageSizeToUse,
      hiddenColumns
    );
    setIsQueryRun(false);
    if (!isLoadMore) {
      resetStateToInitial();
    }
    saveAssignGroupGridReportDataRequest(savePayload, {
      onSuccess: (data) => {
        if (!isLoadMore) {
          paginationSortConfigRef.current =
            createInitialPaginationSortConfig(getDefaultSorting);
          setPaginationConfig(paginationSortConfigRef.current);
        }
        setSavedReportData({
          reportId: data.reportID,
          notificationId: data.notificationID,
          reportFilePath: data.reportFilePath,
          reportName: data.reportName,
          isLoadMore: isLoadMore,
        });
      },
      onError: (error: any) => {
        if (isLoadMore) {
          setIsLoadingMore(false);
          // Revert pageSize on error
          setCurrentPageSize((prev) => prev - PAGE_SIZE_INCREMENT);
        }
        setToastMsgConfig({
          show: true,
          type: ToastType.Error,
          message:
            error?.response?.data?.title ??
            t(STRING_PATH + 'runReportFailedMsg'),
        });
      },
    });
  };

  // Handle Load More button click
  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && savedFilterValues && hasMoreData) {
      setIsLoadingMore(true);
      // Increment pageSize by PAGE_SIZE_INCREMENT
      const nextPageSize = currentPageSize + PAGE_SIZE_INCREMENT;
      setCurrentPageSize(nextPageSize);
      // Call executeSaveAssignGroupGridReportData with incremented pageSize
      // Pass nextPageSize directly to avoid async state update issue
      executeSaveAssignGroupGridReportData(
        savedFilterValues,
        true,
        nextPageSize
      );
    }
  }, [isLoadingMore, savedFilterValues, hasMoreData, currentPageSize]);

  const handleActionClick = async (
    actionKey: string,
    values: Record<string, any>
  ) => {
    if (actionKey === 'applyChanges') {
      setSavedFilterValues(values);
      executeSaveAssignGroupGridReportData(values);
    } else if (actionKey === 'reset') {
      isResetExecuted.current = true;
      if (initialJsonConfig.current && leftPanelData) {
        const transformedData =
          transformLeftPanelDataToReviewResultsSources(leftPanelData);
        // Pass undefined to ensure toggle resets to default (true)
        setReportConfig(
          mergeApiDataIntoConfig(
            initialJsonConfig.current,
            transformedData,
            undefined
          )
        );
      }
      setIsQueryRun(false);
      resetStateToInitial();
      setSavedFilterValues({});
      setCurrentFilterValues({});
      setSavedReportData(createInitialSavedReportData());
      paginationSortConfigRef.current =
        createInitialPaginationSortConfig(getDefaultSorting);
      setPaginationConfig(paginationSortConfigRef.current);
      if (isQueryRun) fetchLeftPanelData();
    }
  };

  // Don't wait for saved report data if it's not loading and we have leftPanelData
  // This handles the case when coming from notification link without ReportLabel
  // Don't show loading if we have assignmentProgressReportData from state (notification case)
  const hasStateData =
    savedReportStateData?.assignmentProgressReportData &&
    Array.isArray(savedReportStateData.assignmentProgressReportData) &&
    savedReportStateData.assignmentProgressReportData.length > 0;

  if (
    _.isNil(reportConfig) ||
    _.isNil(leftPanelData) ||
    isLoadingLeftPanelData ||
    // Only wait for leftpanel-report for saved report cases (with ReportLabel)
    // For notification cases (without ReportLabel), we use state-report data instead
    (isLoadingSavedLeftPanelReport &&
      reportIdFromUrl !== 0 &&
      hasReportLabel &&
      !leftPanelSavedReportData &&
      !leftPanelData) ||
    (isReportStateLoading &&
      reportIdFromUrl !== 0 &&
      !hasReportLabel &&
      !hasStateData &&
      !savedReportStateData)
  )
    return <ArrLoadingSpinner isOpen overlay />;

  return (
    <>
      <StandardReports
        className="rpa-container"
        title={
          location?.state?.fromReportLanding
            ? (location?.state?.item?.label ?? '')
            : (reportConfig?.title ?? '')
        }
        infoContent={reportConfig?.infoContent}
        infoModalTitle={reportConfig?.infoModalTitle ?? ''}
        showInfoIcon={reportConfig?.showInfoIcon ?? false}
        reportsConfig={reportConfig?.config as any}
        data={reportGridData}
        mapRows={(data) => data}
        loading={false}
        columns={columns}
        sources={[]}
        selectedOption={null}
        onAction={handleActionClick}
        onFilterChange={handleFilterChange}
        rightPanelHeaderConfig={rightPanelHeaderConfig}
        enableSave={isQueryRun}
        isQueryRun={isQueryRun}
        disableSourceSelection={true}
        validateActionFromParent
        saveReportConfig={payloadForSaveReport}
        onColumnToggleAction={handleColumnToggleAction}
        getRowId={(row) => row.id}
        savedData={savedDataForRightPanel}
        rightContentComponent={
          rawApiResponse?.assignmentProgressList ? (
            <>
              <GroupedAssignmentTables
                assignmentProgressList={rawApiResponse.assignmentProgressList}
                summaryColumns={summaryColumns}
                detailsColumns={columns}
                getRowId={(row) => row.id}
                showDetailsTable={
                  currentFilterValues.includeAssignmentDetail ?? true
                }
              />
              {hasMoreData && (
                <Box className="load-more-btn">
                  <ArrButton
                    variant="secondary"
                    size="medium"
                    aria-label={t('common.loadMore')}
                    onClick={handleLoadMore}
                    disabled={false}
                    dataTestId="btn-load-more"
                  >
                    {t('common.loadMore', 'Load More')}
                  </ArrButton>
                </Box>
              )}
            </>
          ) : undefined
        }
        gridConfig={{
          primaryKey: 'id',
          totalRecords: totalRecordsCount,
          className: 'rpa-report-grid',
          enableCheckbox: false,
          disableVirtualization: false,
          pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
          itemsPerPage: paginationSortConfigRef.current.pageSize,
          currentPage: paginationSortConfigRef.current.pageNumber,
          sortModel: paginationSortConfigRef.current.sort,
          onSortModelChange: (value) => {
            paginationSortConfigRef.current = {
              ...paginationSortConfigRef.current,
              sort: value,
            };
            setPaginationConfig(paginationSortConfigRef.current);
          },
          setItemsPerPage: (value) => {
            paginationSortConfigRef.current = {
              ...paginationSortConfigRef.current,
              pageSize: value,
              pageNumber: 1,
            };
            setPaginationConfig(paginationSortConfigRef.current);
          },
          onPageChange: (pageNumber, pageSize) => {
            paginationSortConfigRef.current = {
              ...paginationSortConfigRef.current,
              pageNumber,
              pageSize: pageSize ?? paginationSortConfigRef.current.pageSize,
            };
            setPaginationConfig(paginationSortConfigRef.current);
          },
          getRowHeight: () => 'auto',
          entityType: t('common.entries'),
        }}
        observReportConfigUpdate={
          reportIdFromUrl !== 0 && Object.keys(savedFilterValues).length > 0
            ? true
            : false
        }
        selectedFilterValues={
          reportIdFromUrl !== 0 && Object.keys(savedFilterValues).length > 0
            ? savedFilterValues
            : undefined
        }
      />
      {(isSavingReportData || isExportingReport) && (
        <ArrLoadingSpinner isOpen overlay />
      )}
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
      {savedReportData.reportId && savedReportData.notificationId && (
        <ArrWaitToCompleteModalWrapper
          showClose={false}
          reportId={savedReportData.reportId}
          notificationId={savedReportData.notificationId}
          onComplete={async (reportId) => {
            if (
              savedReportData.reportId === reportId &&
              savedReportData.reportFilePath &&
              savedReportData.reportName
            ) {
              try {
                const response = await getReviewReportRequest({
                  reportID: reportId,
                  reportFilePath: savedReportData.reportFilePath,
                  reportName: savedReportData.reportName,
                });
                const isLoadMore = savedReportData.isLoadMore || false;
                const transformedData = transformReviewReportDataWrapper(
                  response,
                  isLoadMore
                );
                setReportGridData(transformedData);
                setIsQueryRun(true);
                if (isLoadMore) {
                  setIsLoadingMore(false);
                }
              } catch (error: any) {
                setToastMsgConfig({
                  show: true,
                  type: ToastType.Error,
                  message:
                    error?.response?.data?.title ??
                    t(STRING_PATH + 'reportAPIError'),
                });
              }
            }
          }}
        />
      )}
    </>
  );
};

export default ReviewProgressAssignmentContainer;
